import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useScrollToSectionOnEdit } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useScrollToSectionOnEdit';
import {
  AiAgentSettingsData,
  AnchorItemKey,
  SECTION_KEYS,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { ButtonV2 } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback, useMemo } from 'react';
import styles from '../../styles.module.scss';
import { HeaderSection, SingleRow } from '../Common';
import { Edit } from './Edit';

const cx = classnames.bind(styles);

type AiAgentSettingsProps = {
  data?: AiAgentSettingsData | undefined;
  isEditable?: boolean;
};

export const AiAgentSettings: FC<AiAgentSettingsProps> = ({
  data,
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const { isEditingSection, setIsEditingSection } = usePayeeDetailDrawer();
  const isEditing = isEditingSection === SECTION_KEYS.AI_AGENT;
  const sectionRef = useScrollToSectionOnEdit(isEditing, SECTION_KEYS.AI_AGENT);

  const { ai_agent_service_settings } = data ?? {};

  const invoiceDownloadAgent = ai_agent_service_settings?.[0];

  const displayValue = useMemo(
    () => (invoiceDownloadAgent?.is_enabled ? t('use') : t('not_use')),
    [invoiceDownloadAgent?.is_enabled, t]
  );

  const onActionButtonClick = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIsEditingSection(SECTION_KEYS.AI_AGENT);
    },
    [setIsEditingSection]
  );

  // Hide the section if ai_agent_service_settings is empty or undefined
  if (!ai_agent_service_settings || ai_agent_service_settings.length === 0) {
    return null;
  }

  if (isEditing) {
    return (
      <div
        ref={sectionRef}
        id={AnchorItemKey.AI_AGENT}
        className={cx('sectionContainer')}
      >
        <Edit data={data} />
      </div>
    );
  }

  return (
    <div
      ref={sectionRef}
      id={AnchorItemKey.AI_AGENT}
      className={cx('sectionContainer')}
    >
      <HeaderSection
        displayName={t('ai_agents_setting')}
        actionButton={
          isEditable && (
            <ButtonV2
              onClick={onActionButtonClick}
              isSecondary
              isDisabled={isEditingSection !== SECTION_KEYS.NULL}
            >
              {t('edit')}
            </ButtonV2>
          )
        }
      />
      <div className={cx('bodySectionContainer')}>
        <SingleRow
          label={t('ai_agents_setting_invoice_download_service_title')}
          value={displayValue}
        />
      </div>
    </div>
  );
};

AiAgentSettings.displayName = 'AiAgentSettings';
